"use client";

import Link from "next/link";
import Image from "next/image";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";

type Slide = {
  id: number;
  title: string;
  subtitle: string;
  description: string;
  buttonText: string;
  buttonLink: string;
  imageSrc: string;
};

export default function AnimatedPromoSlider() {
  const slides: Slide[] = [
    {
      id: 1,
      title: "Premium Collection",
      subtitle: "NEW ARRIVAL",
      description: "Explore our handpicked selection of rare and limited edition spirits from around the world.",
      buttonText: "Shop Collection",
      buttonLink: "/category/spirits",
      imageSrc: "https://images.pexels.com/photos/6063444/pexels-photo-6063444.jpeg?auto=compress&cs=tinysrgb&w=1260"
    },
    {
      id: 2,
      title: "Summer Cocktails",
      subtitle: "SEASONAL SPECIAL",
      description: "Discover our curated ingredients for refreshing summer cocktails perfect for any occasion.",
      buttonText: "View Recipes",
      buttonLink: "/category/cocktails",
      imageSrc: "https://images.pexels.com/photos/3019019/pexels-photo-3019019.jpeg?auto=compress&cs=tinysrgb&w=1260"
    },
    {
      id: 3,
      title: "Exclusive Champagne",
      subtitle: "PERFECT PRESENTS",
      description: "Impress with our luxury gift sets, elegantly packaged and ready to delight any recipient.",
      buttonText: "Exclusive Champagne",
      buttonLink: "/category/champagne",
      imageSrc: "https://images.pexels.com/photos/3323682/pexels-photo-3323682.jpeg?auto=compress&cs=tinysrgb&w=1260"
    }
  ];

  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // Auto-advance slides
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [isAutoPlaying, slides.length]);

  // Pause auto-play on hover
  const handleMouseEnter = () => setIsAutoPlaying(false);
  const handleMouseLeave = () => setIsAutoPlaying(true);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  // Animation variants
  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? "100%" : "-100%",
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1,
      transition: {
        x: { type: "spring", stiffness: 300, damping: 30 },
        opacity: { duration: 0.4 }
      }
    },
    exit: (direction: number) => ({
      x: direction < 0 ? "100%" : "-100%",
      opacity: 0,
      transition: {
        x: { type: "spring", stiffness: 300, damping: 30 },
        opacity: { duration: 0.2 }
      }
    })
  };

  // For content animation
  const contentVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0, 
      transition: { 
        delay: 0.3, 
        duration: 0.6, 
        staggerChildren: 0.1,
        delayChildren: 0.4
      } 
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0, 
      transition: { 
        duration: 0.5 
      } 
    }
  };

  const [direction, setDirection] = useState(0);

  const handleNext = () => {
    setDirection(1);
    nextSlide();
  };

  const handlePrev = () => {
    setDirection(-1);
    prevSlide();
  };

  return (
    <div 
      className="relative w-full h-[500px] mt-20 mb-16 overflow-hidden rounded-xl"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Slides */}
      <AnimatePresence custom={direction} initial={false} mode="wait">
        <motion.div
          key={currentSlide}
          custom={direction}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          className="absolute inset-0 w-full h-full"
        >
          <Image
            src={slides[currentSlide].imageSrc}
            alt={slides[currentSlide].title}
            fill
            priority
            className="object-cover object-center"
            sizes="(max-width: 768px) 100vw, 1200px"
          />
          
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent" />
          
          {/* Content */}
          <motion.div 
            className="absolute inset-0 flex items-center p-6 sm:p-12 lg:p-20"
            variants={contentVariants}
            initial="hidden"
            animate="visible"
          >
            <div className="max-w-xl text-white">
              <motion.span 
                variants={itemVariants}
                className="inline-block mb-3 bg-primary/90 px-4 py-1 rounded-full text-sm font-semibold tracking-wider"
              >
                {slides[currentSlide].subtitle}
              </motion.span>
              
              <motion.h2 
                variants={itemVariants}
                className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 leading-tight"
              >
                {slides[currentSlide].title}
              </motion.h2>
              
              <motion.p 
                variants={itemVariants}
                className="text-lg sm:text-xl opacity-90 mb-8 max-w-md"
              >
                {slides[currentSlide].description}
              </motion.p>
              
              <motion.div variants={itemVariants}>
                <Button asChild size="lg" className="rounded-full px-8">
                  <Link href={slides[currentSlide].buttonLink}>
                    {slides[currentSlide].buttonText}
                  </Link>
                </Button>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>

      {/* Navigation buttons */}
      <div className="absolute bottom-6 right-6 flex items-center space-x-3 z-10">
        <button
          onClick={handlePrev}
          className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 flex items-center justify-center text-white transition-colors"
          aria-label="Previous slide"
        >
          <ChevronLeftIcon size={20} />
        </button>
        <button
          onClick={handleNext}
          className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 flex items-center justify-center text-white transition-colors"
          aria-label="Next slide"
        >
          <ChevronRightIcon size={20} />
        </button>
      </div>

      {/* Slide indicators */}
      <div className="absolute bottom-6 left-6 flex space-x-2 z-10">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => {
              setDirection(index > currentSlide ? 1 : -1);
              setCurrentSlide(index);
            }}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              index === currentSlide 
                ? "w-8 bg-primary" 
                : "bg-white/50 hover:bg-white/70"
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
}
